import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useTimecards, useShifts, useTriggerTimecardSync } from '../hooks/useTimecards';
import { useAllLocations } from '../hooks/useLocations';
import { useToast } from '../lib/toast';
import { matchShift } from '../lib/timecardHelpers';
import type { TeamsTimecard, TeamsShift } from '../api/types';
import '../styles/timecards.css';

// ---------------------------------------------------------------------------
// Pay period helpers (Wed → Tue weekly)
// ---------------------------------------------------------------------------

interface PayPeriod {
  from: Date;
  to: Date;
}

function getPayPeriod(date: Date): PayPeriod {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  // dayOfWeek: 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  // Days since last Wednesday: Wed=0 Thu=1 Fri=2 Sat=3 Sun=4 Mon=5 Tue=6
  const daysFromWed = (d.getDay() + 4) % 7;
  const wed = new Date(d);
  wed.setDate(d.getDate() - daysFromWed);
  const tue = new Date(wed);
  tue.setDate(wed.getDate() + 6);
  tue.setHours(23, 59, 59, 999);
  return { from: wed, to: tue };
}

function prevPeriod(p: PayPeriod): PayPeriod {
  const d = new Date(p.from);
  d.setDate(d.getDate() - 1);
  return getPayPeriod(d);
}

function nextPeriod(p: PayPeriod): PayPeriod {
  const d = new Date(p.to);
  d.setDate(d.getDate() + 1);
  return getPayPeriod(d);
}

function fmtPeriodLabel(p: PayPeriod): string {
  const sameYear = p.from.getFullYear() === p.to.getFullYear();
  const fromStr  = p.from.toLocaleDateString([], { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
  const toStr    = p.to.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fromStr} – ${toStr}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function fmtDuration(inIso: string | null | undefined, outIso: string | null | undefined): string {
  if (!inIso || !outIso) return '—';
  const mins = Math.round((new Date(outIso).getTime() - new Date(inIso).getTime()) / 60_000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ---------------------------------------------------------------------------
// Timecard row
// ---------------------------------------------------------------------------

function TimecardRow({
  card,
  shift,
  showLocation,
  showUser,
  showDate,
}: {
  card: TeamsTimecard;
  shift: TeamsShift | undefined;
  showLocation: boolean;
  showUser: boolean;
  showDate: boolean;
}) {
  const name = card.displayName ?? card.entraObjectId.slice(0, 8);
  const stateBadge = {
    clockedIn:  'tc-badge tc-badge--active',
    onBreak:    'tc-badge tc-badge--break',
    clockedOut: 'tc-badge tc-badge--out',
    confirmed:  'tc-badge tc-badge--confirmed',
  }[card.state] ?? 'tc-badge';

  return (
    <tr className="tc-row">
      {showUser && (
        <td className="tc-cell tc-cell--name">
          <span className="tc-name">{name}</span>
          {!card.userId && <span className="tc-orphan-badge">unmatched</span>}
        </td>
      )}
      {showLocation && <td className="tc-cell">{card.locationName}</td>}
      {showDate && <td className="tc-cell tc-cell--date">{fmtDate(card.clockIn?.dateTime)}</td>}
      <td className="tc-cell tc-cell--time mono">{fmtTime(card.clockIn?.dateTime)}</td>
      <td className="tc-cell tc-cell--time mono">{fmtTime(card.clockOut?.dateTime)}</td>
      <td className="tc-cell tc-cell--dur mono">{fmtDuration(card.clockIn?.dateTime, card.clockOut?.dateTime)}</td>
      <td className="tc-cell tc-cell--breaks">
        {card.breaks.length > 0 ? `${card.breaks.length} break${card.breaks.length > 1 ? 's' : ''}` : '—'}
      </td>
      <td className="tc-cell">
        <span className={stateBadge}>{card.state.replace('clocked', '')}</span>
      </td>
      <td className="tc-cell tc-cell--shift">
        {shift
          ? <span className="tc-shift-match">{fmtTime(shift.sharedStart)} – {fmtTime(shift.sharedEnd)}</span>
          : <span className="tc-shift-none">no shift</span>}
      </td>
      {card.versionCount > 0 && (
        <td className="tc-cell tc-cell--versions">
          <span className="tc-version-badge">{card.versionCount} revision{card.versionCount > 1 ? 's' : ''}</span>
        </td>
      )}
      {card.versionCount === 0 && <td className="tc-cell" />}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function TimecardsTable({
  timecards,
  shifts,
  groupByUser,
  showDate,
}: {
  timecards: TeamsTimecard[];
  shifts: TeamsShift[];
  groupByUser: boolean;
  showDate: boolean;
}) {
  const shiftByCardId = useMemo(() => {
    const map = new Map<string, TeamsShift>();
    for (const c of timecards) {
      const s = matchShift(c, shifts);
      if (s) map.set(c.id, s);
    }
    return map;
  }, [timecards, shifts]);

  if (timecards.length === 0) {
    return <div className="tc-empty">No timecards found for this period.</div>;
  }

  if (groupByUser) {
    const byUser = new Map<string, TeamsTimecard[]>();
    for (const c of timecards) {
      const key = c.userId ?? c.entraObjectId;
      const arr = byUser.get(key) ?? [];
      arr.push(c);
      byUser.set(key, arr);
    }

    return (
      <div className="tc-groups">
        {[...byUser.entries()].map(([key, cards]) => {
          const firstName = cards[0];
          if (!firstName) return null;
          const name = firstName.displayName ?? firstName.entraObjectId.slice(0, 8);
          return (
            <div key={key} className="tc-group">
              <div className="tc-group-header">
                <span className="tc-group-name">{name}</span>
                {!firstName.userId && <span className="tc-orphan-badge">unmatched</span>}
                <span className="tc-group-count">{cards.length} punch{cards.length > 1 ? 'es' : ''}</span>
              </div>
              <table className="tc-table">
                <thead>
                  <tr>
                    <th>Location</th>
                    {showDate && <th>Date</th>}
                    <th>In</th>
                    <th>Out</th>
                    <th>Duration</th>
                    <th>Breaks</th>
                    <th>Status</th>
                    <th>Scheduled shift</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((c) => (
                    <TimecardRow
                      key={c.id}
                      card={c}
                      shift={shiftByCardId.get(c.id)}
                      showLocation
                      showUser={false}
                      showDate={showDate}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }

  // By location
  const byLocation = new Map<string, TeamsTimecard[]>();
  for (const c of timecards) {
    const arr = byLocation.get(c.locationId) ?? [];
    arr.push(c);
    byLocation.set(c.locationId, arr);
  }

  return (
    <div className="tc-groups">
      {[...byLocation.entries()].map(([locId, cards]) => {
        const firstName = cards[0];
        if (!firstName) return null;
        return (
          <div key={locId} className="tc-group">
            <div className="tc-group-header">
              <span className="tc-group-name">{firstName.locationName}</span>
              <span className="tc-group-count">{cards.length} punch{cards.length > 1 ? 'es' : ''}</span>
            </div>
            <table className="tc-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  {showDate && <th>Date</th>}
                  <th>In</th>
                  <th>Out</th>
                  <th>Duration</th>
                  <th>Breaks</th>
                  <th>Status</th>
                  <th>Scheduled shift</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <TimecardRow
                    key={c.id}
                    card={c}
                    shift={shiftByCardId.get(c.id)}
                    showLocation={false}
                    showUser
                    showDate={showDate}
                  />
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ViewMode = 'user' | 'location';

export function TimecardsPage(): React.ReactElement {
  const { toast } = useToast();
  const { data: locData } = useAllLocations();
  const locations = locData?.items ?? [];

  // Period state
  const [period, setPeriod]         = useState<PayPeriod>(() => getPayPeriod(new Date()));
  const [singleDay, setSingleDay]   = useState(false);
  const [selectedDay, setSelectedDay] = useState(() => new Date().toISOString().slice(0, 10));

  // View controls
  const [viewMode, setViewMode]     = useState<ViewMode>('location');
  const [locationFilter, setLocationFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const effectivePeriod: PayPeriod = singleDay
    ? (() => {
        const d = new Date(selectedDay + 'T00:00:00');
        const end = new Date(d); end.setHours(23, 59, 59, 999);
        return { from: d, to: end };
      })()
    : period;

  const tcParams = {
    from: effectivePeriod.from.toISOString(),
    to:   effectivePeriod.to.toISOString(),
    ...(locationFilter ? { locationId: locationFilter } : {}),
  };

  const { data: tcData, isLoading: tcLoading, error: tcError } = useTimecards(tcParams);
  const { data: shData, isLoading: shLoading }                 = useShifts(tcParams);

  const syncMutation = useTriggerTimecardSync();

  const allTimecards = useMemo(() => tcData?.items ?? [], [tcData]);
  const allShifts    = useMemo(() => shData?.items ?? [], [shData]);

  // Filter by user search
  const filteredTimecards = useMemo(() => {
    if (!userSearch.trim() || viewMode !== 'user') return allTimecards;
    const q = userSearch.toLowerCase();
    return allTimecards.filter(
      (c) => c.displayName?.toLowerCase().includes(q) || c.entraObjectId.toLowerCase().includes(q),
    );
  }, [allTimecards, userSearch, viewMode]);

  async function handleSync() {
    try {
      const result = await syncMutation.mutateAsync();
      const counts = `${result.timecardsUpserted} timecards, ${result.shiftsUpserted} shifts updated`;
      if (result.warnings.length > 0) {
        toast.error(`Sync finished with ${result.warnings.length} warning(s) (${counts}): ${result.warnings[0]}`);
      } else {
        toast.success(`Synced: ${counts}`);
      }
    } catch {
      toast.error('Sync failed — check the admin panel for details');
    }
  }

  const isLoading = tcLoading || shLoading;

  return (
    <div className="canto-page">
      {/* Header */}
      <div className="canto-page-header">
        <div>
          <h1>Timecards</h1>
          <div className="page-meta">Teams Shifts · Admin &amp; Payroll</div>
        </div>
        <button
          className="btn btn-sm btn-ghost"
          onClick={handleSync}
          disabled={syncMutation.isPending}
          title="Re-sync all locations from Teams"
        >
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {/* Controls bar */}
      <div className="tc-controls">
        {/* Period navigator */}
        <div className="tc-period-nav">
          {!singleDay && (
            <>
              <button className="icon-btn" onClick={() => setPeriod(prevPeriod(period))} aria-label="Previous period">
                <ChevronLeft size={16} />
              </button>
              <span className="tc-period-label">{fmtPeriodLabel(period)}</span>
              <button
                className="icon-btn"
                onClick={() => setPeriod(nextPeriod(period))}
                disabled={nextPeriod(period).from > new Date()}
                aria-label="Next period"
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
          {singleDay && (
            <input
              type="date"
              className="tc-date-input"
              value={selectedDay}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setSelectedDay(e.target.value)}
            />
          )}
          <div className="seg-ctrl">
            <button className={`seg-btn${!singleDay ? ' active' : ''}`} onClick={() => setSingleDay(false)}>
              Pay period
            </button>
            <button className={`seg-btn${singleDay ? ' active' : ''}`} onClick={() => setSingleDay(true)}>
              Single day
            </button>
          </div>
        </div>

        {/* View mode + filters */}
        <div className="tc-filters">
          <div className="seg-ctrl">
            <button
              className={`seg-btn${viewMode === 'location' ? ' active' : ''}`}
              onClick={() => setViewMode('location')}
            >
              By location
            </button>
            <button
              className={`seg-btn${viewMode === 'user' ? ' active' : ''}`}
              onClick={() => setViewMode('user')}
            >
              By employee
            </button>
          </div>

          {locations.length > 0 && (
            <select
              className="tc-select"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}

          {viewMode === 'user' && (
            <input
              type="search"
              placeholder="Search employee…"
              className="tc-search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
          )}
        </div>
      </div>

      {/* Period label (single day) */}
      {singleDay && (
        <div className="tc-day-label">
          {new Date(selectedDay + 'T00:00:00').toLocaleDateString([], {
            weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
          })}
        </div>
      )}

      {/* Content */}
      {isLoading && <div className="tc-loading">Loading timecards…</div>}
      {!isLoading && tcError && (
        <div className="tc-error">Failed to load timecards. {(tcError as Error).message}</div>
      )}
      {!isLoading && !tcError && (
        <TimecardsTable
          timecards={filteredTimecards}
          shifts={allShifts}
          groupByUser={viewMode === 'user'}
          showDate={!singleDay}
        />
      )}
    </div>
  );
}
